import React, { useState, useMemo, useEffect } from "react";
import styled from "@emotion/styled";
import ChartContainer from "../BaseChart/ChartContainer";
import TableView from "./TableView";
import tableMapper, { setParentHeadersMapping } from "./functions/tableMapper";
import Details from "../../Layout/Details/Details";
import { orderSpecialTypes } from "../../utils/func";
import validateTableConfig from "./validateTableConfig";
import TableVisualizationCollection, {
  removeExcessRows,
} from "./TableVisualizationCollection";
import RoundingSelector from "./RoundingSelector";
import { cloneDeep } from "lodash-es";
import { getDateDifference } from "../../store/actions/queryBuilder/getLatestDate";
import { sortByKey } from "../../utils/dates/dateSorting";
import StickyHeader from "./Elements/StickyHeader";
import { WriteBacksProvider, WRITE_BACKS_TYPES } from "./WriteBacksContext";
import Flex from "../../UI/Flex/Flex";
import GrouppedColumnsSelection from "./TableColumnsSelection/GrouppedColumnsSelection";
import SimpleColumnsSelection from "./TableColumnsSelection/SimpleColumnsSelection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tipper from "../../UI/Tipper/Tipper";
import DrilldownInModal from "./Elements/DrilldownInModal";
import { postMessageFromIframe } from "./functions/dynamicDrillDown";
import { sortDynamicGroupingValues } from "./functions/dynamicSorting";
import { getComparisonModeItem } from "../../Layout/Block/interruptDashboard";

const TitleContainer = styled(Flex)`
  margin-bottom: ${(props) => (props.margin ? "1rem" : 0)};
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  display: flex;
`;

const ShowHideColumnsIcon = styled(FontAwesomeIcon)(
  ({ theme }) => `
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${theme.primary}
  }
`
);

export default function TableVisualization(props) {
  const {
    chart,
    details,
    dashboardId,
    canEdit,
    dateFilters,
    sortPaginated,
    sticky,
    setSticky,
    fromFullDetails,
    menuFilters,
    comparisonMode,
    activeTab,
    offsetTop,
    title: blockTitle,
    limit,
    setLimit,
    rowsQuantity,
    domain,
    isLoading,
  } = props;

  const [dynamicSortedRows, setDynamicSortedRows] = useState(null);

  const {
    data: sourceData,
    loading = true,
    refreshing,
    error,
    visualizationId,
    subTitles = [],
    rowGroupKey,
    groupingKey,
    prefix,
    uniqueValueKey,
    staticGroupingKeys,
    calculatedRows,
    isSortable,
    rollup,
    hideExportToExcel,
    totals,
    regularFormatting,
    rawRows,
    roundingSelector,
    renderAsImageKeys,
    totalsOnly,
    totalsAverages,
    latestDate,
    specialSortingTypes,
    staticHighLevelKeys,
    dynamicHighLevelKeys,
    hideDetailLink,
    pinnedRows,
    groupingDataConfig,
    subTotalRow,
    totalsPositionTop,
    title,
    restricted,
    hideHeaders,
    hideDetails,
    offsetZebra,
    groupingKeyExcessItems,
    nonConvertPercentsBack,
    groupingKeyMapping,
    totalsCharts,
    fixedHeight,
    initialOrderConfig,
    groupingExtraColumns,
    rawRowsValueKey,
    totalColumnsStatic,
    excludeRowFromTotalCalculation,
    stickyHeaders,
    totalsOverride,
    fiscalQuarterStartOffset,
    writeBacks,
    dynamicSubTitleKeys,
    dynamicPostfixColumn,
    displayRows,
    connectWithQueryParameter,
    showHideColumns,
    noResultsMessageOverride,
    globalStickyHeaders,
    groupByKey,
    headerColorConfig,
    noTotalCalcKeys = [],
    ...restChart
  } = chart;

  const [sortKey, setSortKey] = useState(null);
  const [sortUniqueKey, setSortUniqueKey] = useState(null);
  const [rowIndexes, setRowIndexes] = useState([]);
  const [rowData, setRowData] = useState(null);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [showColumnsSidebar, setShowColumnsSidebar] = useState(false);
  const [drilldownLevel, setDrilldownLevel] = useState({});
  const [parentHeaderSort, setParentHeaderSort] = useState({
    header: null,
    direction: null,
  });

  const [hiddenGroupings, setHiddenGroupings] = useState(
    chart.hiddenGroupings || []
  );
  const [hiddenColumns, setHiddenColumns] = useState(chart.hiddenColumns || []);

  useEffect(() => {
    if (chart?.hiddenGroupings) {
      setHiddenGroupings(chart.hiddenGroupings);
    }

    if (chart?.hiddenColumns) {
      setHiddenColumns(chart.hiddenColumns);
    }
  }, [chart?.hiddenGroupings, chart?.hiddenColumns]);

  const [performRounding, setPerformRounding] = useState(!!roundingSelector);
  const configCopy = cloneDeep(chart);

  // ignore strings and some columns in noTotalCalcKeys from total calculaton
  const noCalcKeys = [
    ...noTotalCalcKeys,
    ...(chart?.meta?.fields || [])
      .filter((f) => f.type === "string")
      .map((k) => k.name),
  ];

  let data = cloneDeep(
    removeExcessRows(sourceData, groupingKeyExcessItems, groupingKey)
  )
    .map((item) =>
      setParentHeadersMapping(
        item,
        groupingKeyMapping,
        groupingKey,
        fiscalQuarterStartOffset
      )
    )
    .filter((_, index) => {
      if (connectWithQueryParameter) {
        return true;
      }
      return displayRows ? index < +limit : true;
    });

  validateTableConfig(chart);

  const rowMainKey = defineMainKey();
  const sortedData = sortData();
  const emptyFirstColumnHeader = !groupingKey && !calculatedRows;

  const comparisonModeItem = getComparisonModeItem(
    comparisonMode,
    activeTab?.uuid
  );

  const aTableCollection = useMemo(
    () =>
      new TableVisualizationCollection(
        data,
        sortedData,
        groupingKey,
        rowGroupKey,
        restChart.rowGroupKeys
      ),
    [data, groupingKey, rowGroupKey, sortedData, restChart.rowGroupKeys]
  );

  if (dynamicHighLevelKeys) {
    aTableCollection.setDynamicHighLevelHeaders(
      dynamicHighLevelKeys,
      menuFilters,
      comparisonModeItem
    );
  }

  if (staticHighLevelKeys) {
    aTableCollection.setHighLevelHeaders(staticHighLevelKeys);
  }

  aTableCollection.setSubTitles(
    subTitles,
    null,
    null,
    null,
    restChart.overrides,
    rawRows
  );

  aTableCollection.setHeaders(
    emptyFirstColumnHeader,
    rowMainKey,
    staticGroupingKeys,
    prefix,
    groupingKey,
    groupingKeyMapping
  );

  if (rollup) {
    aTableCollection.setRollupData(rollup, data);
    aTableCollection.setRollupSubTitles(rollup, data);
  }

  tableMapper(
    aTableCollection,
    rowMainKey,
    groupingKey,
    uniqueValueKey,
    emptyFirstColumnHeader,
    totals,
    totalsAverages,
    pinnedRows,
    groupingDataConfig,
    subTotalRow,
    totalsPositionTop,
    totalsCharts,
    groupingExtraColumns,
    rawRowsValueKey,
    totalColumnsStatic,
    excludeRowFromTotalCalculation,
    totalsOverride,
    restChart.progressBarsSettings,
    noCalcKeys,
    restChart.rowGroupKeys,
    restChart.totalLabel
  );

  // for dynamic Sub Titles we need to update all subtitles based on dynamic data
  if (dynamicSubTitleKeys) {
    aTableCollection.setSubTitles(
      subTitles,
      dynamicSubTitleKeys,
      groupingKeyMapping,
      dynamicPostfixColumn
    );
  }

  function defineMainKey() {
    // migrate to factory for Table Classes
    if (rollup) return "rowValue";
    if (rawRows) return null;

    function firstKeyAvailable() {
      return Object.keys(data[0])[0];
    }
    return !rowGroupKey && data?.length ? firstKeyAvailable() : rowGroupKey;
  }

  function sortData() {
    if (sortPaginated || rowGroupKey) {
      return data;
    }

    if (initialOrderConfig) {
      const { column, order, type } = initialOrderConfig;
      return orderSpecialTypes(data, column, order, type);
    }

    return isSortable && sortKey
      ? sortByKey(
          sortKey,
          sortOrder,
          regularFormatting,
          specialSortingTypes
        )([...data])
      : data;
  }

  const { highLevelHeaders, headers, rows, progressBarTotals, postfixes } =
    aTableCollection;

  const preparedDataForTableView = {
    highLevelHeaders,
    headers,
    rows: dynamicSortedRows || rows,
    progressBarTotals,
    postfixes,
  };

  // drop local state sorted data on source data change
  useEffect(() => {
    if (sourceData && rowGroupKey) {
      setDynamicSortedRows(null);
      setParentHeaderSort({ header: null, direction: null });
    }
  }, [sourceData, rowGroupKey]);

  const sortDynamicGrouping = (value, direction, dynamicSubTitleKey) => {
    const sortedRows = sortDynamicGroupingValues(
      rows,
      dynamicSubTitleKey ?? dynamicSubTitleKeys[0],
      groupingKey,
      value,
      direction,
      pinnedRows,
      groupingKeyMapping
    );

    setDynamicSortedRows(sortedRows);
  };

  const handleSort = (item, key, index) => {
    const direction = sortKey !== item || sortOrder === "ASC" ? "DESC" : "ASC";
    setSortOrder(direction);
    setSortUniqueKey(key);
    setSortKey(item);

    if (rowGroupKey) {
      sortDynamicGrouping(headers[index], direction, item);
    }

    if (sortPaginated) {
      sortPaginated(item, direction);
    }
  };

  if (totalsOnly) {
    const key = uniqueValueKey || groupByKey || rowMainKey;
    const totalRow = preparedDataForTableView.rows.filter((row) =>
      (row.values ?? []).find((v) => v[key] === "Total")
    );

    preparedDataForTableView.rows = totalRow;
  }

  const hasData = getDateDifference(latestDate, dateFilters);

  const showDetails =
    hasData && details && chart.data.length > 0 && !hideDetails;

  const hasChartData = () => {
    if (hasData && data) {
      const condition =
        chart.writeBacks?.type === WRITE_BACKS_TYPES.crm ||
        chart.writeBacks === WRITE_BACKS_TYPES.crm;

      return !!(data.length || condition);
    }

    return false;
  };

  const onZoomClose = () => {
    setRowData(null);
    if (chart.zoomInDrilldownConfig.attachedURLParameter) {
      postMessageFromIframe(chart.zoomInDrilldownConfig.attachedURLParameter);
    }
  };

  return (
    <>
      <TitleContainer justifyContent="space-between" margin={showHideColumns}>
        <Title>{title}</Title>
        {showHideColumns && (
          <Tipper label="Show/Hide columns">
            <ShowHideColumnsIcon
              icon={["far", "columns"]}
              onClick={() => setShowColumnsSidebar(true)}
            />
          </Tipper>
        )}
      </TitleContainer>
      <div
        id={visualizationId}
        style={{ position: "relative", width: props.width }}
      >
        <StickyHeader
          sticky={sticky}
          setSticky={setSticky}
          stickyHeaders={stickyHeaders}
          loading={loading}
          limit={limit}
          displayRows={displayRows}
          setLimit={setLimit}
          rowsQuantity={rowsQuantity}
          selectedMenuFilter={chart.selectedMenuFilter}
        />
        <ChartContainer
          autoHeight
          hasData={hasChartData()}
          loading={loading}
          refreshing={refreshing}
          error={error}
          restricted={restricted}
          fixedHeight={fixedHeight}
          noResultsMessageOverride={noResultsMessageOverride}
          width={props.width}
        >
          <WriteBacksProvider
            configInput={writeBacks}
            queryUuid={
              writeBacks && writeBacks.querySaveId
                ? writeBacks.querySaveId
                : chart.queryId
            }
          >
            <TableView
              {...restChart}
              data={preparedDataForTableView}
              rowGroupKey={rowMainKey}
              groupingKey={groupingKey}
              subTitles={aTableCollection.columnKeys}
              staticGroupingKeys={staticGroupingKeys}
              valueKeyAsSubTitle={!groupingKey && !calculatedRows}
              calculatedRows={calculatedRows}
              isSortable={isSortable}
              setSortKey={handleSort}
              sortKey={sortUniqueKey}
              sortOrder={sortOrder}
              rollup={rollup}
              canEdit={canEdit}
              exportId={chart.exportId || visualizationId}
              totals={totals}
              performRounding={performRounding}
              regularFormatting={regularFormatting}
              renderAsImageKeys={renderAsImageKeys}
              chart={configCopy}
              pinnedRows={pinnedRows}
              rowIndexes={rowIndexes}
              setRowIndexes={setRowIndexes}
              subTotalRow={subTotalRow}
              totalsPositionTop={totalsPositionTop}
              hideHeaders={hideHeaders}
              offsetZebra={offsetZebra}
              segmentTitle={props.segmentTitle}
              groupingExtraColumns={groupingExtraColumns}
              stickyHeaders={globalStickyHeaders ?? sticky}
              fromFullDetails={fromFullDetails}
              activeTab={activeTab}
              dateFilters={dateFilters}
              menuFilters={menuFilters}
              groupingKeyMapping={groupingKeyMapping}
              offsetTop={globalStickyHeaders ? offsetTop : 0}
              title={title || blockTitle}
              setRowData={setRowData}
              rowData={rowData}
              hiddenGroupings={hiddenGroupings}
              hiddenColumns={hiddenColumns}
              dynamicSubTitleKeys={dynamicSubTitleKeys}
              sortDynamicGrouping={sortDynamicGrouping}
              headerColorConfig={headerColorConfig}
              drilldownLevel={drilldownLevel}
              setDrilldownLevel={setDrilldownLevel}
              rowMainKey={rowMainKey}
              noTotalCalcKeys={noTotalCalcKeys}
              isLoading={isLoading}
              parentHeaderSort={parentHeaderSort}
              setParentHeaderSort={setParentHeaderSort}
            />
          </WriteBacksProvider>

          <RoundingSelector
            show={roundingSelector}
            active={performRounding}
            setActive={setPerformRounding}
          />
        </ChartContainer>
      </div>
      {showDetails && (
        <div style={{ marginBottom: 12 }}>
          <Details
            dashboardName={dashboardId}
            visualizationId={visualizationId}
            exportId={hideExportToExcel ? null : visualizationId}
            hideDetailLink={hideDetailLink}
            rowIndexes={rowIndexes}
            setRowIndexes={setRowIndexes}
            nonConvertPercentsBack={nonConvertPercentsBack}
            title={title || props.title || props.segmentTitle}
            chart={chart}
            activeTab={activeTab}
            dateFilters={dateFilters}
            menuFilters={menuFilters}
            isTableVisualization
            domain={domain}
          />
        </div>
      )}

      {rowData && (
        <DrilldownInModal
          rowData={rowData}
          onClose={onZoomClose}
          chart={{
            ...chart.zoomInDrilldownConfig,
            data: rowData,
            meta: chart.meta,
          }}
        />
      )}

      {preparedDataForTableView.headers?.length > 0 ? (
        <GrouppedColumnsSelection
          isOpened={showColumnsSidebar}
          meta={chart.meta}
          allGroups={preparedDataForTableView.headers}
          hiddenGroupings={hiddenGroupings}
          allColumns={aTableCollection.columnKeys}
          hiddenColumns={hiddenColumns}
          onClose={() => setShowColumnsSidebar(false)}
          hiddenGroupsChange={(headers) => setHiddenGroupings(headers)}
          hiddenColumnsChange={(columns) => setHiddenColumns(columns)}
        />
      ) : (
        <SimpleColumnsSelection
          isOpened={showColumnsSidebar}
          meta={chart.meta}
          allColumns={aTableCollection.columnKeys}
          hiddenColumns={hiddenColumns}
          onClose={() => setShowColumnsSidebar(false)}
          hiddenColumnsChange={(columns) => setHiddenColumns(columns)}
        />
      )}
    </>
  );
}
